/**
 * MediationBoard — the standalone NEUTRAL mediation surface.
 *
 * Court-mandated mediation is a THIRD audience (mediator + both parties). The
 * neutrality the backend gates enforce in the MATH is undone by information
 * architecture if the board lives inside a party workspace, so this is its own
 * route with neutral chrome — NOT the attorney/accountant app shell (owner ruling
 * 2026-09-10, docs/plans/2026-09-10-carrier-side-mediator-prelit-scope.md).
 *
 * Read-only for now (pᵢ flip by accountant/stipulated is deferred until the
 * interpretation_object layer). Renders the neutral best estimate E[X] = N − Σ
 * pᵢ·Cᵢ via <MediatorView>; the "Copy for the room" button lives in that
 * component. Fetches brief.mediator_view; a 404 is the honest "not run yet" state.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getBrief, type Brief } from '@/services/deliberationApi';
import { MediatorView } from '@/components/war-room/MediatorView';

const shortId = (s?: string | null) => (s ? (s.length > 12 ? `${s.slice(0, 8)}…${s.slice(-4)}` : s) : '—');
const shortRun = (s?: string | null) => (s ? (s.length > 30 ? `${s.slice(0, 30)}…` : s) : '—');

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen" style={{ background: 'var(--color-bg-base)', padding: '24px 16px' }}>
      <div style={{ maxWidth: 760, margin: '0 auto' }}>{children}</div>
    </div>
  );
}

export default function MediationBoard() {
  const { reportId } = useParams<{ reportId: string }>();
  const [brief, setBrief] = useState<Brief | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [noBrief, setNoBrief] = useState(false);

  const fetchData = useCallback(async () => {
    if (!reportId) return;
    setLoading(true); setError(null); setNoBrief(false);
    try {
      const res = await getBrief(reportId);
      setBrief(res.brief);
    } catch (err: any) {
      const status = err?.response?.status ?? err?.status;
      const empty = status === 404 || /no brief (found|available)/i.test(err?.message || '');
      if (empty) setNoBrief(true);
      else setError(err?.message || 'Failed to load the mediation board.');
    } finally {
      setLoading(false);
    }
  }, [reportId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const mv = brief?.mediator_view || null;
  const runId = mv?.blocks?.neutral?.run_id || brief?.run_id || null;
  const bare = !(mv?.blocks?.neutral?.p_source && mv.blocks.neutral.p_source !== 'none');

  return (
    <Shell>
      {/* Neutral header — case · run · BARE RECORD · not a party demand */}
      <div
        className="flex items-baseline justify-between gap-3 flex-wrap"
        style={{ borderBottom: '1px solid var(--color-border)', paddingBottom: 12, marginBottom: 20 }}
      >
        <div>
          <h1 className="cs-display" style={{ fontSize: 22, lineHeight: 1.1 }}>Mediation Board</h1>
          <div className="cs-body" style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 2 }}>
            Neutral best estimate · <span style={{ color: 'var(--color-text-primary)' }}>not a party demand</span>
          </div>
        </div>
        <div className="text-right font-mono" style={{ fontSize: 10, color: 'var(--color-text-dim)', lineHeight: 1.5 }}>
          <div>case {shortId(reportId)}</div>
          <div>run {shortRun(runId)}</div>
          {mv && (
            <div className="uppercase" style={{ letterSpacing: '0.06em', color: 'var(--color-text-muted)', marginTop: 2 }}>
              {bare ? 'Bare record · read-only' : 'Evidence entered · read-only'}
            </div>
          )}
        </div>
      </div>

      {loading && (
        <div className="cs-body" style={{ color: 'var(--color-text-secondary)', fontSize: 13, padding: '24px 0' }}>
          Loading the mediation board…
        </div>
      )}

      {!loading && error && (
        <div style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 2, padding: 20 }}>
          <div className="cs-body" style={{ color: 'var(--color-text-primary)', fontSize: 14 }}>Couldn't load the board</div>
          <div className="cs-body" style={{ color: 'var(--color-text-secondary)', fontSize: 12, marginTop: 4 }}>{error}</div>
          <button
            type="button" onClick={fetchData}
            className="font-mono uppercase"
            style={{ marginTop: 12, fontSize: 10, letterSpacing: '0.08em', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border)', borderRadius: 2, padding: '4px 10px', background: 'transparent', cursor: 'pointer' }}
          >
            Retry
          </button>
        </div>
      )}

      {!loading && !error && noBrief && (
        <div style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 2, padding: 20 }}>
          <div className="cs-body" style={{ color: 'var(--color-text-primary)', fontSize: 14 }}>No neutral estimate yet</div>
          <div className="cs-body" style={{ color: 'var(--color-text-secondary)', fontSize: 12, marginTop: 4 }}>
            The deliberation has not produced a brief for this case. Nothing is fabricated in its absence.
          </div>
        </div>
      )}

      {!loading && !error && !noBrief && (
        mv
          ? <MediatorView data={mv} />
          : (
            <div style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 2, padding: 20 }}>
              <div className="cs-body" style={{ color: 'var(--color-text-primary)', fontSize: 14 }}>Neutral estimate unavailable</div>
              <div className="cs-body" style={{ color: 'var(--color-text-secondary)', fontSize: 12, marginTop: 4 }}>
                This brief predates the mediator view, or its engine inputs are absent. Nothing composed, nothing fabricated.
              </div>
            </div>
          )
      )}
    </Shell>
  );
}
